import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../utils/api";
import { Shield, CheckCircle, XCircle, Clock, History } from "lucide-react";

const AdminKYC = () => {
  const [tab, setTab] = useState("pending"); // "pending" | "history"
  const [historyFilter, setHistoryFilter] = useState("Approved"); // "Approved" | "Rejected"

  const [kycQueue, setKycQueue] = useState([]);
  const [kycHistory, setKycHistory] = useState([]);
  const [selectedKycDetail, setSelectedKycDetail] = useState(null); // full card data
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Fetch when tab or historyFilter changes
  useEffect(() => {
    if (tab === "pending") {
      fetchKYCQueue();
    } else {
      fetchKYCHistory(historyFilter);
    }
  }, [tab, historyFilter]);

  // Fetch pending KYC queue
  const fetchKYCQueue = async () => {
    setLoading(true);
    try {
      // backend must support ?status=Pending
      const res = await api.get("/admin/kyc", {
        params: { status: "Pending" },
      });
      setKycQueue(res.data || []);
      console.log("/admin/kyc?status=Pending:", res.data);
    } catch (err) {
      console.error("Failed to fetch KYC queue:", err);
    }
    setLoading(false);
  };

  // Fetch Approved / Rejected KYCs for history
  const fetchKYCHistory = async (status) => {
    setLoading(true);
    try {
      // backend must support ?status=Approved / Rejected
      const res = await api.get("/admin/kyc", {
        params: { status },
      });
      setKycHistory(res.data || []);
      console.log(`/admin/kyc?status=${status}:`, res.data);
    } catch (err) {
      console.error("Failed to fetch KYC history:", err);
    }
    setLoading(false);
  };

  const handleApprove = async (kycId, status) => {
    if (!kycId) return;
    setProcessing(true);
    try {
      await api.post(`/admin/kyc/${kycId}/approve`, {
        status,
        remarks: remarks || undefined,
      });
      alert(`KYC ${status.toLowerCase()} successfully!`);
      setSelectedKycDetail(null);
      setRemarks("");
      // refresh list
      if (tab === "pending") {
        fetchKYCQueue();
      } else {
        fetchKYCHistory(historyFilter);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to process KYC");
    }
    setProcessing(false);
  };

  const openKycCard = (kyc) => {
    setSelectedKycDetail(kyc);
    setRemarks("");
  };

  const closeKycCard = () => {
    if (processing) return; // don't allow close while submitting
    setSelectedKycDetail(null);
    setRemarks("");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  const renderStatusBadge = (status) => {
    if (status === "Approved") {
      return (
        <span className="bg-success-light text-success text-xs font-semibold px-3 py-1 rounded-full">
          Approved
        </span>
      );
    }
    if (status === "Rejected") {
      return (
        <span className="bg-error-light text-error text-xs font-semibold px-3 py-1 rounded-full">
          Rejected
        </span>
      );
    }
    return (
      <span className="bg-warning-light text-warning text-xs font-semibold px-3 py-1 rounded-full">
        Pending
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 relative">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900">
              KYC Review
            </h1>
            <p className="text-gray-600 mt-1">
              {tab === "pending"
                ? `${kycQueue.length} pending reviews`
                : `${kycHistory.length} reviewed (${historyFilter})`}
            </p>
          </div>

          {/* Top Tabs */}
          <div className="flex gap-3 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setTab("pending")}
              className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
                tab === "pending"
                  ? "bg-white shadow text-primary"
                  : "text-gray-600 hover:text-primary"
              }`}
            >
              <Clock className="w-4 h-4" /> Pending
            </button>
            <button
              onClick={() => setTab("history")}
              className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
                tab === "history"
                  ? "bg-white shadow text-primary"
                  : "text-gray-600 hover:text-primary"
              }`}
            >
              <History className="w-4 h-4" /> Last KYC List
            </button>
          </div>
        </div>

        {/* === Pending KYCs LIST === */}
        {tab === "pending" && (
          <>
            {kycQueue.length > 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Header row */}
                <div className="grid grid-cols-4 gap-4 px-4 py-3 border-b bg-gray-50 text-xs font-semibold text-gray-600">
                  <div>Name</div>
                  <div>TAI ID</div>
                  <div>Status</div>
                  <div>Phone</div>
                </div>

                {/* Rows */}
                {kycQueue.map((kyc) => (
                  <button
                    key={kyc.id}
                    onClick={() => openKycCard(kyc)}
                    className="w-full text-left grid grid-cols-4 gap-4 px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 focus:outline-none"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900">
                        {kyc.userName || "—"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {kyc.userEmail || ""}
                      </span>
                    </div>
                    <div className="flex items-center text-sm font-mono text-gray-800">
                      {kyc.taiId || "—"}
                    </div>
                    <div className="flex items-center">
                      {renderStatusBadge(kyc.status)}
                    </div>
                    <div className="flex items-center text-sm text-gray-800">
                      {kyc.userPhone || "—"}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="col-span-2 text-center py-12">
                <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">
                  No pending KYC reviews
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  All KYC submissions have been processed
                </p>
              </div>
            )}
          </>
        )}

        {/* === KYC History === */}
        {tab === "history" && (
          <div className="space-y-4">
            {/* Inner tabs: Approved / Rejected */}
            <div className="flex gap-2">
              <button
                onClick={() => setHistoryFilter("Approved")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  historyFilter === "Approved"
                    ? "bg-primary text-white"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Approved
              </button>
              <button
                onClick={() => setHistoryFilter("Rejected")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  historyFilter === "Rejected"
                    ? "bg-primary text-white"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Rejected
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {kycHistory.length > 0 ? (
                kycHistory.map((kyc) => (
                  <div
                    key={kyc.id}
                    className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-sm"
                    onClick={() => openKycCard(kyc)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {kyc.userName}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {kyc.userEmail}
                        </p>
                        <p className="text-sm text-gray-600">
                          {kyc.userPhone}
                        </p>
                      </div>
                      {renderStatusBadge(kyc.status)}
                    </div>

                    <div className="space-y-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">Remarks</p>
                        <p className="font-mono text-sm text-gray-800">
                          {kyc.remarks || "—"}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        Updated:{" "}
                        {kyc.updatedAt
                          ? new Date(kyc.updatedAt).toLocaleString()
                          : "—"}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center py-12">
                  <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">
                    No {historyFilter.toLowerCase()} KYC records yet
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Once reviewed, {historyFilter.toLowerCase()} KYCs will
                    appear here
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === DETAIL CARD (MODAL) === */}
        {selectedKycDetail && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={closeKycCard}
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-3xl w-full mx-4 p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                onClick={closeKycCard}
                disabled={processing}
              >
                <XCircle className="w-6 h-6" />
              </button>

              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedKycDetail.userName || "KYC Details"}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {selectedKycDetail.userEmail}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedKycDetail.userPhone}
                  </p>
                </div>
                <div className="text-right space-y-1">
                  {renderStatusBadge(selectedKycDetail.status)}
                  <p className="text-xs text-gray-500">
                    User ID: {selectedKycDetail.userId || "—"}
                  </p>
                  <p className="text-xs text-gray-500">
                    TAI ID: {selectedKycDetail.taiId || "—"}
                  </p>
                </div>
              </div>

              {/* Basic info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Aadhaar Number</p>
                  <p className="font-mono text-sm font-semibold">
                    {selectedKycDetail.aadhaarNumber || "Not provided"}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">PAN Number</p>
                  <p className="font-mono text-sm font-semibold">
                    {selectedKycDetail.panNumber || "Not provided"}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Created At</p>
                  <p className="text-sm text-gray-800">
                    {selectedKycDetail.createdAt
                      ? new Date(
                          selectedKycDetail.createdAt
                        ).toLocaleString()
                      : "—"}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Updated At</p>
                  <p className="text-sm text-gray-800">
                    {selectedKycDetail.updatedAt
                      ? new Date(
                          selectedKycDetail.updatedAt
                        ).toLocaleString()
                      : "—"}
                  </p>
                </div>
              </div>

              {/* Documents */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Documents
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {selectedKycDetail.documents?.aadhaar_front && (
                    <a
                      href={selectedKycDetail.documents.aadhaar_front}
                      target="_blank"
                      rel="noreferrer"
                      className="block"
                    >
                      <div className="border rounded-lg overflow-hidden">
                        <img
                          src={selectedKycDetail.documents.aadhaar_front}
                          alt="Aadhaar Front"
                          className="w-full h-28 object-cover"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-600">
                        Aadhaar Front
                      </p>
                    </a>
                  )}
                  {selectedKycDetail.documents?.aadhaar_back && (
                    <a
                      href={selectedKycDetail.documents.aadhaar_back}
                      target="_blank"
                      rel="noreferrer"
                      className="block"
                    >
                      <div className="border rounded-lg overflow-hidden">
                        <img
                          src={selectedKycDetail.documents.aadhaar_back}
                          alt="Aadhaar Back"
                          className="w-full h-28 object-cover"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-600">
                        Aadhaar Back
                      </p>
                    </a>
                  )}
                  {selectedKycDetail.documents?.pan_card && (
                    <a
                      href={selectedKycDetail.documents.pan_card}
                      target="_blank"
                      rel="noreferrer"
                      className="block"
                    >
                      <div className="border rounded-lg overflow-hidden">
                        <img
                          src={selectedKycDetail.documents.pan_card}
                          alt="PAN Card"
                          className="w-full h-28 object-cover"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-600">PAN Card</p>
                    </a>
                  )}
                  {selectedKycDetail.documents?.selfie && (
                    <a
                      href={selectedKycDetail.documents.selfie}
                      target="_blank"
                      rel="noreferrer"
                      className="block"
                    >
                      <div className="border rounded-lg overflow-hidden">
                        <img
                          src={selectedKycDetail.documents.selfie}
                          alt="Selfie"
                          className="w-full h-28 object-cover"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-600">Selfie</p>
                    </a>
                  )}
                  {!selectedKycDetail.documents && (
                    <p className="text-xs text-gray-500 col-span-full">
                      No documents uploaded
                    </p>
                  )}
                </div>
              </div>

              {/* Remarks + Actions (only for Pending) */}
              {selectedKycDetail.status === "Pending" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Remarks (Optional)
                    </label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Add remarks for rejection or notes..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() =>
                        handleApprove(selectedKycDetail.id, "Approved")
                      }
                      disabled={processing}
                      className="flex-1 bg-success hover:bg-success-dark text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:bg-gray-400"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Approve
                    </button>
                    <button
                      onClick={() =>
                        handleApprove(selectedKycDetail.id, "Rejected")
                      }
                      disabled={processing}
                      className="flex-1 bg-error hover:bg-error-dark text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:bg-gray-400"
                    >
                      <XCircle className="w-5 h-5" />
                      Reject
                    </button>
                  </div>
                </>
              )}

              {selectedKycDetail.status !== "Pending" && (
                <p className="mt-3 text-xs text-gray-500">
                  This KYC is already {selectedKycDetail.status}. Actions are
                  disabled.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminKYC;
